// src/components/business/BusinessNotificationsPanel.tsx
// Mirrors mobile business notification logic — fetches from
// GET /api/businesses/:id/notifications/all and displays a slide-in panel.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, X, Package, Calendar, ShoppingBag,
  Star, RefreshCw, AlertTriangle, Info, CheckCircle, Loader2,
} from 'lucide-react';
import apiClient from '@/services/apiClient';

// ── Types matching backend response ──────────────────────────────────────────

interface NotificationItem {
  count: number;
  severity: 'warning' | 'info' | 'none';
}

interface NotificationsData {
  total_actionable: number;
  notifications: {
    low_stock:              NotificationItem;
    new_bookings:           NotificationItem;
    pending_website_orders: NotificationItem;
    unpaid_delivered:       NotificationItem;
    unresponded_reviews:    NotificationItem;
  };
  timestamp: string;
}

interface Props {
  businessId: string;
}

// ── Severity helpers ──────────────────────────────────────────────────────────

const severityColor = (s: string) =>
  s === 'warning' ? '#F59E0B' : s === 'info' ? '#0EA5E9' : '#10B981';

const SeverityIcon: React.FC<{ severity: string; size?: number }> = ({ severity, size = 16 }) => {
  const color = severityColor(severity);
  if (severity === 'warning') return <AlertTriangle size={size} style={{ color }} />;
  if (severity === 'info')    return <Info          size={size} style={{ color }} />;
  return                             <CheckCircle   size={size} style={{ color }} />;
};

// ── Single notification row ───────────────────────────────────────────────────

const NotifRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  severity: string;
  description: string;
  onClick: () => void;
}> = ({ icon, label, count, severity, description, onClick }) => {
  if (count === 0) return null;
  const color = severityColor(severity);

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-left border-b border-light-border dark:border-[#1e1e1e] last:border-0"
    >
      {/* icon circle */}
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}18` }}
      >
        {icon}
      </div>

      {/* text */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-light-text dark:text-dark-text">{label}</p>
        <p className="text-xs text-light-subtle dark:text-dark-subtle truncate">{description}</p>
      </div>

      {/* badge */}
      <span
        className="flex-shrink-0 min-w-[22px] h-[22px] px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
        style={{ backgroundColor: color }}
      >
        {count > 99 ? '99+' : count}
      </span>
    </button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const BusinessNotificationsPanel: React.FC<Props> = ({ businessId }) => {
  const navigate = useNavigate();
  const [open, setOpen]         = useState(false);
  const [data, setData]         = useState<NotificationsData | null>(null);
  const [loading, setLoading]   = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const fetch = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/businesses/${businessId}/notifications/all`);
      setData(res.data);
      setLastFetched(new Date());
    } catch {
      // silently fail — badge just won't show
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  // Fetch on mount and every 5 minutes
  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetch]);

  const total = data?.total_actionable ?? 0;
  const n     = data?.notifications;

  const go = (path: string) => { setOpen(false); navigate(path); };

  return (
    <>
      {/* ── Bell button ── */}
      <button
        onClick={() => { setOpen(true); if (!data) fetch(); }}
        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors relative"
        title="Business notifications"
      >
        <Bell size={20} className="text-light-text dark:text-dark-text" />

        {/* Live badge — only shown when there are actionable items */}
        {total > 0 && (
          <span
            className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
            style={{ backgroundColor: '#EF4444' }}
          >
            {total > 99 ? '99+' : total}
          </span>
        )}

        {/* Static red dot when no count yet but loading */}
        {total === 0 && loading && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-danger" />
        )}
      </button>

      {/* ── Backdrop ── */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setOpen(false)}
        />
      )}

      {/* ── Slide-in panel — anchored top-right below header ── */}
      {open && (
        <div
          className="fixed top-[65px] right-4 z-50 w-80 rounded-2xl overflow-hidden shadow-2xl"
          style={{
            backgroundColor: 'var(--panel-bg)',
            animation: 'notif-slide 0.18s ease-out',
          }}
        >
          <style>{`
            @keyframes notif-slide {
              from { opacity: 0; transform: translateY(-10px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0)     scale(1);    }
            }
          `}</style>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-[#1e1e1e]">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-primary" />
              <span className="text-sm font-bold text-light-text dark:text-dark-text">Notifications</span>
              {total > 0 && (
                <span className="text-[11px] font-bold text-white bg-danger px-1.5 py-0.5 rounded-full">
                  {total}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={fetch}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                title="Refresh"
              >
                <RefreshCw size={13} className={`text-light-subtle dark:text-dark-subtle ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              >
                <X size={15} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="bg-light-card dark:bg-dark-card">
            {loading && !data ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={24} className="animate-spin text-primary" />
              </div>
            ) : !n || total === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <CheckCircle size={22} className="text-primary" />
                </div>
                <p className="text-sm font-semibold text-light-text dark:text-dark-text">All caught up!</p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">No pending actions for your business.</p>
              </div>
            ) : (
              <div>
                {/* Low stock */}
                <NotifRow
                  icon={<Package size={16} style={{ color: severityColor(n.low_stock.severity) }} />}
                  label="Low Stock Alert"
                  count={n.low_stock.count}
                  severity={n.low_stock.severity}
                  description={`${n.low_stock.count} product${n.low_stock.count !== 1 ? 's' : ''} running low or out of stock`}
                  onClick={() => go(`/business/${businessId}/barcodes`)}
                />

                {/* New bookings */}
                <NotifRow
                  icon={<Calendar size={16} style={{ color: severityColor(n.new_bookings.severity) }} />}
                  label="New Bookings Today"
                  count={n.new_bookings.count}
                  severity={n.new_bookings.severity}
                  description={`${n.new_bookings.count} pending booking${n.new_bookings.count !== 1 ? 's' : ''} awaiting confirmation`}
                  onClick={() => go(`/business/${businessId}/bookings`)}
                />

                {/* Pending website orders */}
                <NotifRow
                  icon={<ShoppingBag size={16} style={{ color: severityColor(n.pending_website_orders.severity) }} />}
                  label="Website Orders"
                  count={n.pending_website_orders.count}
                  severity={n.pending_website_orders.severity}
                  description={`${n.pending_website_orders.count} new order${n.pending_website_orders.count !== 1 ? 's' : ''} awaiting your contact`}
                  onClick={() => go(`/business/${businessId}/orders`)}
                />

                {/* Unpaid delivered */}
                <NotifRow
                  icon={<ShoppingBag size={16} style={{ color: severityColor(n.unpaid_delivered.severity) }} />}
                  label="Unpaid Deliveries"
                  count={n.unpaid_delivered.count}
                  severity={n.unpaid_delivered.severity}
                  description={`${n.unpaid_delivered.count} delivered order${n.unpaid_delivered.count !== 1 ? 's' : ''} with pending payment`}
                  onClick={() => go(`/business/${businessId}/orders`)}
                />

                {/* Unresponded reviews */}
                <NotifRow
                  icon={<Star size={16} style={{ color: severityColor(n.unresponded_reviews.severity) }} />}
                  label="Unresponded Reviews"
                  count={n.unresponded_reviews.count}
                  severity={n.unresponded_reviews.severity}
                  description={`${n.unresponded_reviews.count} customer review${n.unresponded_reviews.count !== 1 ? 's' : ''} waiting for your reply`}
                  onClick={() => go(`/business/${businessId}/reviews`)}
                />
              </div>
            )}

            {/* Footer — last updated */}
            {lastFetched && (
              <div className="px-4 py-2 border-t border-light-border dark:border-[#1e1e1e]">
                <p className="text-[10px] text-light-subtle dark:text-dark-subtle text-center">
                  Updated {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessNotificationsPanel;
